/**
 * Last-sync status for everyone; "Sync now" + latest run detail for admins and managers
 * (`/v1/me.permissions.canSync`, `POST /v1/sync`, `GET /v1/sync/runs`).
 */
import { useCallback } from 'react'

import { isApiError } from '@/api/client'
import type { Runway, SyncStatus } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import { useMe } from '@/features/auth/MeContext'
import { formatDateTime, formatIsoDay, formatRelative } from '@/lib/format'

import styles from './SyncPanel.module.css'
import { useRefreshOnSyncComplete, useSyncRuns, useTriggerSync } from './useSync'

const STATUS_TEXT: Record<SyncStatus, string> = {
  ok: 'Synced',
  failed: 'Last sync failed',
  running: 'Sync running…',
  never: 'Never synced',
}

/** User-facing text for a failed `POST /v1/sync` (409 in flight, 429 cooldown). */
function triggerErrorText(error: unknown): string | null {
  if (!error) return null
  if (!isApiError(error)) return 'Could not start a sync.'
  if (error.status === 409) return 'A sync is already running.'
  if (error.status === 429) {
    const body = error.body as { retry_after_seconds?: unknown } | null
    const seconds = typeof body?.retry_after_seconds === 'number' ? body.retry_after_seconds : null
    const wait = seconds === null ? 'in a few minutes' : `in about ${Math.max(1, Math.ceil(seconds / 60))} min`
    return `A sync was started recently. Try again ${wait}.`
  }
  return 'Could not start a sync.'
}

function StatusLine({ status, at }: { status: SyncStatus; at: string | null }) {
  const when = status === 'running' || status === 'never' ? null : at
  return (
    <div className={styles.status} title={when ? formatDateTime(when) : undefined}>
      <span className={`${styles.dot} ${styles[status]}`} aria-hidden="true" />
      <span>
        {STATUS_TEXT[status]}
        {when ? ` ${formatRelative(when)}` : ''}
      </span>
    </div>
  )
}

/** "Data through Oct 14, 2026": the latest entry date the figures include (future-dated lines wait). */
function DataThrough({ value }: { value: string | null }) {
  if (!value) return null
  return (
    <div
      className={styles.through}
      title="Latest GL entry date included. Lines dated later count once that date arrives."
    >
      Data through {formatIsoDay(value)}
    </div>
  )
}

interface SyncPanelProps {
  lastSync: Runway['last_sync']
  dataThrough?: string | null
}

function AdminSync({ lastSync, dataThrough = null }: SyncPanelProps) {
  const runs = useSyncRuns(true)
  const trigger = useTriggerSync()
  const latest = runs.data?.[0]
  useRefreshOnSyncComplete(latest)

  const { mutate } = trigger
  const handleSync = useCallback(() => mutate(), [mutate])

  const status: SyncStatus = latest?.status ?? lastSync.status
  const at = latest ? (latest.finished_at ?? latest.started_at) : lastSync.finished_at
  const running = status === 'running' || trigger.isPending
  const triggerError = triggerErrorText(trigger.error)

  return (
    <div className={styles.panel} aria-live="polite">
      <StatusLine status={status} at={at} />
      <DataThrough value={dataThrough} />
      {latest?.status === 'failed' && latest.error ? <div className={styles.error}>{latest.error}</div> : null}
      {triggerError ? <div className={styles.error}>{triggerError}</div> : null}
      <button type="button" className={styles.button} onClick={handleSync} disabled={running}>
        <ICONS.refresh size={13} className={running ? styles.spin : undefined} />
        {running ? 'Syncing…' : 'Sync now'}
      </button>
    </div>
  )
}

export function SyncPanel({ lastSync, dataThrough = null }: SyncPanelProps) {
  const me = useMe()
  if (me.permissions.canSync) return <AdminSync lastSync={lastSync} dataThrough={dataThrough} />
  return (
    <div className={styles.panel}>
      <StatusLine status={lastSync.status} at={lastSync.finished_at} />
      <DataThrough value={dataThrough} />
    </div>
  )
}
